import { type ConsentPayload, type ConsentSource } from '@liveauctioneers/hammer-ui-core/cookieConsent';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents, TrackingPayload } from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

type CookiePreferencesSourcePayload = TrackingPayload & {
    source: ConsentSource;
};

type CookiePreferencesSavedPayload = TrackingPayload & {
    consent: ConsentPayload;
    source: ConsentSource;
};

const trackCookieBannerViewedAnalytics = (payload: TrackingPayload) => {
    track(TrackingEvents.CookieConsent.CookieBannerViewed, payload);
};

const trackCookiePreferencesOpenedAnalytics = (payload: CookiePreferencesSourcePayload) => {
    track(TrackingEvents.CookieConsent.CookiePreferencesOpened, payload);
};

const trackCookiePreferencesSavedAnalytics = (payload: CookiePreferencesSavedPayload) => {
    track(TrackingEvents.CookieConsent.CookiePreferencesSaved, payload);
};

const trackCookiePreferencesDismissedAnalytics = (payload: CookiePreferencesSourcePayload) => {
    track(TrackingEvents.CookieConsent.CookiePreferencesDismissed, payload);
};

export const useTrackCookieConsentAnalytics = () => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    const onBannerViewed = useCallback(() => {
        trackCookieBannerViewedAnalytics({
            pageName: getAmplitudePageNameFromPath(pathname),
            url,
        });
    }, [pathname, url]);

    const onPreferencesOpened = useCallback(
        (source: ConsentSource) => {
            trackCookiePreferencesOpenedAnalytics({
                pageName: getAmplitudePageNameFromPath(pathname),
                source,
                url,
            });
        },
        [pathname, url]
    );

    const onPreferencesSaved = useCallback(
        ({ consent, source }: { consent: ConsentPayload; source: ConsentSource }) => {
            trackCookiePreferencesSavedAnalytics({
                consent,
                pageName: getAmplitudePageNameFromPath(pathname),
                source,
                url,
            });
        },
        [pathname, url]
    );

    const onPreferencesDismissed = useCallback(
        (source: ConsentSource) => {
            trackCookiePreferencesDismissedAnalytics({
                pageName: getAmplitudePageNameFromPath(pathname),
                source,
                url,
            });
        },
        [pathname, url]
    );

    return {
        onBannerViewed,
        onPreferencesDismissed,
        onPreferencesOpened,
        onPreferencesSaved,
    };
};
